import { adminByIdQuery, adminCreate, adminUpdate } from '@/entities/admins'
import type {
  AdminModel,
  CreateAdminModel,
  UpdateAdminModel,
} from '@/shared/api'
import { createModalModel } from '@/shared/lib/model-factories'
import { createEvent, sample } from 'effector'
import { createGate } from 'effector-react'
import { or } from 'patronum'

// Initialize

type AdminFormGateProps = {
  isModal?: boolean
  onSubmitted?: (id: string) => void
  resetForm?: () => void
}

export const AdminFormGate = createGate<AdminFormGateProps>()

// Modal

export const {
  modalOpened: adminFormModalOpened,
  modalClosed: adminFormModalClosed,
  $isModalOpened: $isAdminFormModalOpened,
} = createModalModel()

const adminFormModalOpenedForEdit = createEvent<{ id: string }>()
sample({
  clock: adminFormModalOpened,
  filter: (params): params is { id: string } => Boolean(params),
  target: adminFormModalOpenedForEdit,
})

sample({
  clock: adminFormModalOpenedForEdit,
  target: adminByIdQuery.start,
})
sample({
  source: AdminFormGate.state,
  clock: adminFormModalClosed,
  fn: (state) => {
    state.resetForm?.()
  },
})

sample({
  source: adminByIdQuery.$data,
  clock: adminFormModalClosed,
  target: adminByIdQuery.reset,
})
// Submit

export const $submittingAdmin = or(adminCreate.$pending, adminUpdate.$pending)

export const adminCreated = createEvent<CreateAdminModel>()
sample({
  source: adminByIdQuery.$data,
  clock: adminCreated,
  filter: (admin: AdminModel | null): admin is null => admin === null,
  fn: (
    _,
    adminSubmittedData,
  ): {
    data: CreateAdminModel
  } => {
    return { data: adminSubmittedData }
  },
  target: adminCreate.start,
})

export const adminUpdated = createEvent<UpdateAdminModel>()
sample({
  source: adminByIdQuery.$data,
  clock: adminUpdated,
  filter: (admin: AdminModel | null): admin is AdminModel =>
    admin !== null && typeof admin.id === 'string',
  fn: (admin, adminSubmittedData) => {
    return {
      id: admin.id,
      data: adminSubmittedData,
    }
  },
  target: adminUpdate.start,
})

// OnSubmitted

sample({
  clock: [adminCreate.finished.success, adminUpdate.finished.success],
  source: AdminFormGate.state,
  filter: (gateState) => Boolean(gateState.isModal),
  fn: (gateState, { result }) => {
    if (result?.id && gateState.onSubmitted) {
      gateState.onSubmitted(result.id)
    }
  },
  target: adminFormModalClosed,
})
